import { generateReferenceSlug } from './referenceSlug';
import type { AppErrorContext, AppErrorParams, ErrorKind, ErrorTier } from './types';

/**
 * Base class for the six typed application errors. Carries a user-safe message,
 * a machine `code`, retry policy, optional field/context, and a human-readable
 * `referenceSlug`. The active `traceId` is stamped at throw time by the boundary
 * catch (Yoga plugin / tracedStep), NOT in the constructor, because the active
 * span is only available where the throw happens.
 */
export abstract class AppError extends Error {
  abstract readonly kind: ErrorKind;
  abstract readonly tier: ErrorTier;

  readonly code: string;
  readonly userMessage: string;
  readonly suggestion?: string;
  readonly retryable: boolean;
  readonly field?: string | string[];
  readonly context?: AppErrorContext;
  readonly referenceSlug: string;
  /** Underlying error. Declared here (not via Error's ES2022 `cause` option) so
   *  it type-checks under shared's TS lib target. */
  readonly cause?: unknown;

  /** Stamped by the boundary catch at throw time (mutable by design). */
  traceId?: string;

  constructor(params: AppErrorParams) {
    super(params.userMessage);
    this.name = this.constructor.name;
    this.code = params.code;
    this.userMessage = params.userMessage;
    this.suggestion = params.suggestion;
    this.retryable = params.retryable ?? this.defaultRetryable();
    this.field = params.field;
    this.context = params.context;
    this.referenceSlug = generateReferenceSlug();
    this.cause = params.cause;
  }

  /** Default per-class retry policy. Subclasses override. */
  protected abstract defaultRetryable(): boolean;

  /** Whether the surface should show the reference code (slug + hex). */
  get isReferenceVisible(): boolean {
    if (this.tier === 'system') return true;
    if (this.kind === 'integration') return true;
    if (this.kind === 'conflict' && !this.retryable) return true;
    return false;
  }

  /** Serialize for Knock data, Pino logs, GraphQL extensions. Return type is
   *  the permissive `Record` so subclasses (e.g. QboSyncError, which serializes
   *  its own annotations for a DB round-trip) can override it. */
  toJSON(): Record<string, unknown> {
    return {
      code: this.code,
      kind: this.kind,
      tier: this.tier,
      userMessage: this.userMessage,
      suggestion: this.suggestion,
      retryable: this.retryable,
      field: this.field,
      context: this.context,
      referenceSlug: this.referenceSlug,
      traceId: this.traceId,
    };
  }
}

export const isAppError = (e: unknown): e is AppError => e instanceof AppError;
